import React, { FC } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
} from '@mui/material';
import { getServiceName } from 'organisms/InvoicesTable/helpers';
import {
  ServicesWrapper,
  StyledServiceTableText,
  StyledServiceTableTitle,
} from './Services.styles';
import {
  StyledDetailsContainer,
  StyledLinkButton,
} from 'organisms/CloudOpsReportAdmin/CloudOpsReportAdmin.styles';
import Link from 'atoms/Link/Link';
import { StyledDetailsText } from 'molecules/Table/styles/Text.styles';

import { roundUp } from 'utils/helpers/formValidators';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import { useBaoSelector } from 'utils/hooks/redux';
import { ADMIN, SUPER_ADMIN } from 'utils/constants/roles';
import { Row } from '../types';
import { useTenant } from 'tenants/TenantProvider';
import { getRuntimeTenantUrl } from 'tenants/tenantConfigService';

interface IProps {
  row: Row;
  onOpenLog?: (r: Row) => void;
  onOpenAdjustment?: (r: Row) => void;
}

const Services: FC<IProps> = ({ row, onOpenLog, onOpenAdjustment }) => {
  const { value: user } = useBaoSelector((state) => state.user);
  const { branding } = useTenant();
  const isSuperAdmin = user.userType === SUPER_ADMIN;
  const isAnyAdmin = user.userType === SUPER_ADMIN || user.userType === ADMIN;

  return (
    <ServicesWrapper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <StyledServiceTableTitle>Services</StyledServiceTableTitle>
            </TableCell>
            <TableCell sx={{ maxWidth: '620px' }}>
              <StyledServiceTableTitle>Description</StyledServiceTableTitle>
            </TableCell>
            <TableCell>
              <StyledServiceTableTitle $right>Amount</StyledServiceTableTitle>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row.services.map((item) => (
            <TableRow key={`service-${item.amount}`}>
              <TableCell>
                <StyledServiceTableText>{item.name}</StyledServiceTableText>
              </TableCell>
              <TableCell>
                <StyledServiceTableText>
                  {item.description?.substring(0, 4000)}
                  {item.description?.length > 4000 ? '...' : ''}
                </StyledServiceTableText>
              </TableCell>
              <TableCell>
                <StyledServiceTableText $right>
                  {currencyFormatter(
                    Number(roundUp(item.amount)) || 0,
                    row.currency
                  )}
                </StyledServiceTableText>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ p: '0 !important' }} colSpan={100}>
              <Grid container spacing={0} sx={{ p: '8px 24px' }}>
                <Grid item xs>
                  <StyledDetailsContainer $firstCol>
                    {row?.invoiceProjectReportId &&
                    row.invoiceProjectReportId > 0 &&
                    row?.isSystemCreated &&
                    onOpenLog &&
                    isAnyAdmin ? (
                      <StyledLinkButton onClick={() => onOpenLog(row)}>
                        View time logs details
                      </StyledLinkButton>
                    ) : (
                      ''
                    )}
                    {row?.invoiceProjectReportId &&
                    row.adjustment_amount &&
                    row?.isSystemCreated &&
                    onOpenAdjustment &&
                    isAnyAdmin ? (
                      <StyledLinkButton onClick={() => onOpenAdjustment(row)}>
                        Adjustment time logs details
                      </StyledLinkButton>
                    ) : (
                      ''
                    )}
                    {row.stripeLink && isAnyAdmin && (
                      <Link
                        to={row.stripeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View payment in Stripe
                      </Link>
                    )}
                    {row.taskLink &&
                      (!row.companyDisableAccessToZstream || isSuperAdmin) && (
                        <Link
                          to={row.taskLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`View Tasks ${getServiceName(row.taskLink)}`}
                        </Link>
                      )}
                    {row.invoiceType === 'CO' && row?.isSystemCreated && (
                      <Link
                        to={
                          getRuntimeTenantUrl('observability') ||
                          window.location.origin
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Observability
                      </Link>
                    )}
                  </StyledDetailsContainer>
                </Grid>
                <Grid item xs="auto">
                  <StyledDetailsContainer>
                    <StyledDetailsText>Subtotal</StyledDetailsText>
                    <StyledDetailsText>
                      {branding.discountLabel}
                    </StyledDetailsText>
                    <StyledDetailsText>Tax</StyledDetailsText>
                    {row.status.state === 'paid' &&
                      Number(row.transactionFee) > 0 && (
                        <StyledDetailsText>
                          Payment Processing Fee
                        </StyledDetailsText>
                      )}
                    <StyledDetailsText $dividerTop $bold>
                      Total
                    </StyledDetailsText>
                    {Number(row.paymentsCredits || 0) > 0 &&
                      Number(row.paymentsCredits || 0) <
                        Number(row.invoiceTotal || 0) && (
                        <StyledDetailsText $bold>Paid</StyledDetailsText>
                      )}
                    <StyledDetailsText $bold>Amount Due</StyledDetailsText>
                  </StyledDetailsContainer>
                </Grid>
                <Grid item xs="auto" minWidth={120}>
                  <StyledDetailsContainer>
                    <StyledDetailsText>
                      {currencyFormatter(Number(row.subtotal), row.currency)}
                    </StyledDetailsText>
                    <StyledDetailsText>
                      {currencyFormatter(
                        Number(row.zazmicDiscount),
                        row.currency
                      )}
                    </StyledDetailsText>
                    <StyledDetailsText>
                      {currencyFormatter(
                        Number(row.totalTaxAmount),
                        row.currency
                      )}
                    </StyledDetailsText>
                    {row.status.state === 'paid' &&
                      Number(row.transactionFee) > 0 && (
                        <StyledDetailsText>
                          {currencyFormatter(
                            Number(row.transactionFee),
                            row.currency
                          )}
                        </StyledDetailsText>
                      )}
                    <StyledDetailsText $dividerTop $bold>
                      {currencyFormatter(
                        Number(row.invoiceTotal),
                        row.currency
                      )}
                    </StyledDetailsText>
                    {Number(row.paymentsCredits || 0) > 0 &&
                      Number(row.paymentsCredits || 0) <
                        Number(row.invoiceTotal || 0) && (
                        <StyledDetailsText $bold>
                          {currencyFormatter(
                            Number(row.paymentsCredits),
                            row.currency
                          )}
                        </StyledDetailsText>
                      )}
                    <StyledDetailsText $bold>
                      {currencyFormatter(Number(row.amountDue), row.currency)}
                    </StyledDetailsText>
                  </StyledDetailsContainer>
                </Grid>
              </Grid>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </ServicesWrapper>
  );
};

export default Services;
